import mixins from '@/mixins'
import validation from '@/mixins/validation'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { debounce, snakeCase } from 'lodash'
import moment from 'moment'
import ImageFile from '@/components/image-file/index.vue'

// Keys handled explicitly in createRequestPayload (typed-widget fields).
// They must be skipped in the generic `value || ''` loop so that boolean
// `false`, empty arrays and nested objects are not serialized incorrectly.
const TYPED_KEYS = ['type', 'variables', 'courseIds', 'showOnLessons', 'startsAt', 'content']

export default {
  name: 'setting-widgets-general',
  mixin: [mixins, validation],
  components: {
    ImageFile,
    CodeEditor: () => import('@/components/code-editor/index.vue')
  },
  data () {
    return {
      valid: false,
      search: null,
      searchInput: null,
      courseSearchInput: null,
      payload: {
        name: '',
        statusId: '',
        key: '',
        content: ''
      },
      // Typed-widget fields kept outside `payload` so the generic serialization
      // loop (`value || ''`) never touches them — see createRequestPayload.
      type: 'custom_html',
      variables: this.customHtmlSafeDefaults(this.defaultVariables()),
      courseIds: [],
      // {id, name} snapshots of every selected course, so chips keep readable
      // names even when `searchResult` is replaced by a new keyword search.
      selectedCourseItems: [],
      showOnLessons: true,
      startsAt: '',
      widgetTypes: [
        { value: 'custom_html', text: 'Custom HTML' },
        { value: 'topbar', text: 'Top bar' },
        { value: 'footer_banner', text: 'Footer banner' },
        { value: 'footer_promo', text: 'Footer promo' },
        { value: 'generic', text: 'Generic' }
      ],
      dismissScopes: [
        { value: 'view', text: 'Every visit (legacy banner behavior)' },
        { value: 'session', text: 'Session' },
        { value: 'day', text: 'Day' },
        { value: 'forever', text: 'Forever' }
      ],
      positions: [
        { value: 'top', text: 'Top (in-flow bar)' },
        { value: 'bottom', text: 'Bottom (fixed footer)' }
      ],
      // Kept outside `payload` on purpose: createRequestPayload sends every
      // payload key as `value || ''`, so an empty disable_in_minutes would
      // be posted on every save and reset scheduled_disabled_at on the
      // backend.
      disableInMinutes: null,
      disableInMinutesRules: [
        v => v == null || v === '' || (Number.isInteger(+v) && +v >= 1 && +v <= 10080) || '1..10080'
      ]
    }
  },
  watch: {
    currentItem () {
      if (this.currentItem && this.editId) {
        this.setData()
      }
    },
    'payload.statusId' (val) {
      if (val !== 2) this.disableInMinutes = null
    },
    type (val) {
      // switching to custom_html: closable stays opt-in (see setData note)
      if (val === 'custom_html' && !this.hasStoredVariables(this.currentItem)) {
        this.variables.closable = false
      }
    },
    courseIds () {
      this.rememberSelectedCourses()
    }
  },
  computed: {
    ...mapGetters({
      profile: 'profile/profile',
      isLoading: 'settingWidgets/isLoading',
      currentItem: 'settingWidgets/currentItem',
      settings: 'templates/settings',
      list: 'settingWidgets/list',
      editId: 'settingWidgets/editId',

      basicCountries: 'setting/basicCountries',

      searchResult: 'courses/searchResult',
      searchListLoading: 'courses/searchListLoading'
    }),
    scheduledDisabledAt () {
      return this.currentItem ? this.currentItem.scheduledDisabledAt : null
    },
    // Image uploads (footer_banner / footer_promo) require a persisted widget
    // id to associate the file with — on a brand-new widget there is none yet,
    // so the uploader is replaced by a hint prompting the operator to save first.
    canUploadImages () {
      return !!(this.currentItem && this.currentItem.id)
    },
    // Merge fetched search results with any pre-selected courses so their
    // chips keep rendering readable names after an edit is reopened.
    courseItems () {
      const items = [...this.searchResult]
      const known = new Set(items.map(e => Number(e.id)))
      const extras = [
        ...this.selectedCourseItems,
        ...(this.currentItem && Array.isArray(this.currentItem.courses)
          ? this.currentItem.courses
          : [])
      ]

      extras.forEach(c => {
        if (!known.has(Number(c.id))) {
          items.push(c)
          known.add(Number(c.id))
        }
      })

      return items
    }
  },
  created () {
    if (this.currentItem) {
      this.setData()
    }
    if (!this.settings) this.fetchSettings()

	this.$on('event-save', () =>{
	  this.submit();
	});
  },
  methods: {
    ...mapActions({
      update: 'settingWidgets/UPDATE',
      create: 'settingWidgets/CREATE',
      fetchSettings: 'templates/GET_SETTING',
      fetchSearchCourses: 'courses/SEARCH_COURSES'
    }),
    ...mapMutations({}),
    // For custom_html closable must be opt-in even on brand-new records:
    // a default-ON switch would silently break freshly created script widgets
    // (closable routes them out of the legacy path where <script> executes).
    customHtmlSafeDefaults (variables) {
      if ((this.type || 'custom_html') === 'custom_html') {
        variables.closable = false
      }
      return variables
    },
    hasStoredVariables (item) {
      return !!(item && item.variables && Object.keys(item.variables).length)
    },
    defaultVariables () {
      return {
        text: '',
        linkUrl: '',
        bgColor: '',
        alt: '',
        closable: true,
        dismissScope: 'session',
        imageDesktop: '',
        imageMobile: '',
        desktopOnly: false,
        buttonText: '',
        buttonColor: '',
        textColor: '',
        position: 'top'
      }
    },
    setData () {
      Object.assign(this.payload, this.currentItem)

      this.type = this.currentItem.type || 'custom_html'
      this.courseIds = Array.isArray(this.currentItem.courseIds)
        ? this.currentItem.courseIds.map(Number)
        : []
      this.showOnLessons = this.currentItem.showOnLessons !== false
      // The picker (vuetify-datetime-picker) can't parse the backend's ISO
      // string — it silently renders an EMPTY field while the stale value
      // keeps being re-posted on save. Hand it a local-time Date instead, so
      // the operator sees the real scheduled time (in their own zone) and
      // formattedDate() converts it back to UTC symmetrically.
      this.startsAt = this.currentItem.startsAt
        ? moment(this.currentItem.startsAt).toDate()
        : ''
      this.variables = Object.assign(this.defaultVariables(), this.currentItem.variables || {})

      // Legacy custom_html records (script widgets) have no stored variables;
      // the shared default closable=true would silently pull them out of the
      // legacy render path on a plain re-save — force opt-in instead.
      if (this.type === 'custom_html' && !this.hasStoredVariables(this.currentItem)) {
        this.variables.closable = false
      }

      this.hydrateSelectedCourses()
    },
    // On reopening a saved widget only ids are stored, so the autocomplete has
    // no items to render chips from. Fetch the missing courses by id
    // (`filters[id]` on courses/search) and seed them into selectedCourseItems
    // so the chips show course names right away.
    hydrateSelectedCourses () {
      const missing = this.courseIds
        .filter(id => !this.courseItems.some(e => Number(e.id) === Number(id)))

      if (!missing.length) return

      this.fetchSearchCourses({ ids: missing }).then(() => {
        const known = new Set(this.selectedCourseItems.map(e => Number(e.id)))
        const found = this.searchResult
          .filter(e => missing.includes(Number(e.id)) && !known.has(Number(e.id)))
          .map(e => ({ id: Number(e.id), name: e.name }))

        if (found.length) {
          this.selectedCourseItems = [...this.selectedCourseItems, ...found]
        }
      })
    },
    // Keep an {id, name} snapshot for every selected id (and drop deselected
    // ones) so chips survive searchResult being replaced by keyword searches.
    rememberSelectedCourses () {
      const remembered = new Map(this.selectedCourseItems.map(e => [Number(e.id), e]))

      this.selectedCourseItems = this.courseIds.reduce((acc, id) => {
        const item = remembered.get(Number(id)) ||
          this.courseItems.find(e => Number(e.id) === Number(id))

        return item ? [...acc, { id: Number(item.id), name: item.name }] : acc
      }, [])
    },
    buildVariables () {
      const v = this.variables

      if (this.type === 'custom_html') {
        // custom_html only carries the closable/dismiss typing — the markup
        // itself lives in `content`. closable=true routes it through
        // widget-host on the frontend and out of the legacy `widgets` key,
        // where <script> tags no longer execute — so it MUST be opt-in:
        // legacy script widgets re-saved untouched keep closable=false.
        return {
          closable: v.closable === true,
          dismiss_scope: v.dismissScope || 'session'
        }
      }

      if (this.type === 'topbar') {
        return {
          text: v.text || '',
          link_url: v.linkUrl || null,
          bg_color: v.bgColor || null,
          closable: v.closable !== false,
          dismiss_scope: v.dismissScope || 'session'
        }
      }

      if (this.type === 'footer_banner') {
        return {
          image_desktop: v.imageDesktop || '',
          image_mobile: v.imageMobile || '',
          desktop_only: v.desktopOnly === true,
          link_url: v.linkUrl || '',
          bg_color: v.bgColor || null,
          alt: v.alt || null,
          closable: v.closable !== false,
          dismiss_scope: v.dismissScope || 'session'
        }
      }

      if (this.type === 'footer_promo') {
        return {
          text: v.text || '',
          button_text: v.buttonText || '',
          link_url: v.linkUrl || '',
          bg_color: v.bgColor || null,
          button_color: v.buttonColor || null,
          text_color: v.textColor || null,
          image_desktop: v.imageDesktop || null,
          image_mobile: v.imageMobile || null,
          closable: v.closable !== false,
          dismiss_scope: v.dismissScope || 'session'
        }
      }

      if (this.type === 'generic') {
        return {
          text: v.text || '',
          button_text: v.buttonText || null,
          link_url: v.linkUrl || null,
          bg_color: v.bgColor || null,
          text_color: v.textColor || null,
          position: v.position || 'top',
          closable: v.closable !== false,
          dismiss_scope: v.dismissScope || 'session'
        }
      }

      return null
    },
    // Always emit the backend's canonical datetime format (Y-m-d H:i:s) in UTC.
    // The backend stores this string as UTC, so formatting in the operator's
    // local zone would shift the date by the UTC offset on EVERY re-save (a
    // +03:00 operator re-saving an untouched record pushed starts_at 3 hours
    // into the future each time). The picker may yield a Date, an ISO string
    // or (on manual edits) an unparseable value — guard with isValid() so we
    // never post 'Invalid date', which would only bounce back as a 422 from
    // the `date` rule.
    formattedDate (date) {
      if (!date) return ''
      const parsed = moment(date)
      return parsed.isValid() ? parsed.utc().format('YYYY-MM-DD HH:mm:ss') : ''
    },
    changeCourseSearch: debounce(function (val) {
      if (val && val.length) {
        this.fetchSearchCourses({ keywords: val })
      }
    }, 500),
    onDesktopImageUpdate (formData) {
      const src = formData.get('src')
      if (src) this.variables.imageDesktop = src
    },
    onMobileImageUpdate (formData) {
      const src = formData.get('src')
      if (src) this.variables.imageMobile = src
    },
    appendTypedFields (formData) {
      formData.append('type', this.type)

      if (this.type === 'custom_html') {
        formData.append('content', this.payload.content || '')
      }

      // Every type (custom_html included) now carries typed `variables` so the
      // closable/dismiss_scope typing reaches the backend.
      formData.append('variables', JSON.stringify(this.buildVariables()))

      formData.append('course_ids', JSON.stringify((this.courseIds || []).map(Number)))
      formData.append('show_on_lessons', this.showOnLessons ? 1 : 0)
      // Append starts_at ONLY when a date was picked; sending '' would post an
      // empty string the backend would have to treat as "clear", which is not
      // the intent when the operator simply left it untouched. Skip entirely
      // when the value can't be formatted, so we never post 'Invalid date'.
      if (this.startsAt) {
        const startsAt = this.formattedDate(this.startsAt)
        if (startsAt) formData.append('starts_at', startsAt)
      }
    },
    createRequestPayload () {
      const formData = new FormData()

      for (const i in this.payload) {
        if (TYPED_KEYS.includes(i)) continue
        formData.append(snakeCase(i), this.payload[i] || '')
      }

      this.appendTypedFields(formData)

      if (this.disableInMinutes !== null && this.disableInMinutes !== '') {
        formData.append('disable_in_minutes', String(this.disableInMinutes))
      }

      this.currentItem ? formData.append('_method', 'patch') : ''
      return formData
    },
    clearAutoDisable () {
      // Bypass form validation — the input rule rejects 0 (since 0 isn't a
      // meaningful "delay"), but `disable_in_minutes=0` is exactly the sentinel
      // the backend uses to clear the timer. Build the request directly.
      if (!this.currentItem) return

      const formData = new FormData()

      for (const i in this.payload) {
        if (TYPED_KEYS.includes(i)) continue
        formData.append(snakeCase(i), this.payload[i] || '')
      }

      this.appendTypedFields(formData)

      formData.append('disable_in_minutes', '0')
      formData.append('_method', 'patch')

      this.update(formData).then(() => {
        this.$toasted.success(this.$t('success_updated'))
        this.disableInMinutes = null
        this.setData()
      })
    },
    submit () {
      this.$refs.form.validate()
      // Mobile image is required for an existing footer_banner unless the
      // operator explicitly opted out via "Mobile banner not needed". Only
      // enforced on edit — on first create images cannot be uploaded yet.
      if (
        this.type === 'footer_banner' &&
        this.currentItem &&
        this.variables.imageDesktop &&
        !this.variables.imageMobile &&
        this.variables.desktopOnly !== true
      ) {
        this.$toasted.error("Upload mobile image or check 'Mobile banner not needed'")
        return
      }
      if (this.valid) {
        if (this.currentItem) {
          this.update(this.createRequestPayload()).then(() => {
            this.$toasted.success(this.$t('success_updated'))
            this.setData();
          });

        } else {
          this.create(this.createRequestPayload()).then(() => {
            this.$toasted.success(this.$t('success_created'));
		  });
        }
      }
    }
  }
}
